import { FC, useEffect, useState } from 'react';
import { EXPORT_FORMATS } from '../../constants/system-constants';
import { useLocale } from '../../contexts/localeContext';
import { useSearch } from '../../contexts/table/SearchContext';
import { useTable } from '../../contexts/table/TableContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import CardIcon from '../../images/CardIcon.svg';
import csvIcon from '../../images/CSV.svg';
import downloadIcon from '../../images/download_button_color.svg';
import filterIcon from '../../images/filter_color.svg';
import pdfIcon from '../../images/PDF.svg';
import CardProvisionIcon from '../../images/provision.png';
import { MultiSelectOption } from '../../models/MultiSelectOption';
import { SearchTerm } from '../../models/search/SearchTerm';
import DropdownSelector from '../dropdown-selector/DropdownSelector';
import SearchField from '../search-field/search-field';

type Props = {
    setSearchTerm: (searchTerm) => void,
    setSearchColumn: (searchColumn) => void,
    toggleFilterModal: (trigger: string) => void,
    columns: SearchTerm[],
    generateReport?: (e) => void,
    onTagSearchOpen?: () => void,
    newSearchTerm?: SearchTerm,
    showFilterMenu: boolean,
    cyName?: string,
    onCardProvision?:() => void;
}

const SearchBar: FC<Props> = ({ setSearchTerm, setSearchColumn, toggleFilterModal, columns, children, generateReport = () => { }, onTagSearchOpen, newSearchTerm, showFilterMenu, cyName, onCardProvision }) => {
    const { translate } = useLocale();
    const tableContext = useTable();
    const downloadOptions: MultiSelectOption[] = tableContext.exportVisibleData ? [
        { value: 'pdf_header', label: translate.byKey(TranslationKeys.pdf), isHeader: true },
        { value: EXPORT_FORMATS.PDF, label: translate.byKey(TranslationKeys.allRows)},
        { value: 'expanded_pdf', label: translate.byKey(TranslationKeys.expandedRows) },
        { value: 'csv_header', label: translate.byKey(TranslationKeys.excel), isHeader: true },
        { value: EXPORT_FORMATS.XLS, label: translate.byKey(TranslationKeys.allRows) },
        { value: 'expanded_xls', label: translate.byKey(TranslationKeys.expandedRows) },
    ] : [
        { value: EXPORT_FORMATS.PDF, label: translate.byKey(TranslationKeys.pdf), iconUrl: pdfIcon },
        { value: EXPORT_FORMATS.XLS, label: translate.byKey(TranslationKeys.excel), iconUrl: csvIcon },
    ];
    const [searchDescriptorOptions, setSearchDescriptorOptions] = useState<any>();
    const [initialSelectValue, setInitialSelectValue] = useState<any>('');

    const buildColumnSearchDropDown = (descriptor) => {
        const searchOptions = descriptor.map((column, index) => {
            return <option value={column.searchOnName} key={'search-bar-' + index} > {translate.byKey(TranslationKeys[column.translationKey])}</option>;
        })
        return searchOptions;
    }

    useEffect(() => {
        columns.map((column, index) => {
            if (index === 0) {
                setInitialSelectValue(column.searchOnName);
            }
        })
        setSearchDescriptorOptions([]);
        setSearchDescriptorOptions(buildColumnSearchDropDown(columns));
    }, [columns]);

    // start of search context
    const searchContext = useSearch();

    const handleSearch = (newSearchValue) => {
        if (searchContext?.isInUse) {
            searchContext.updateSearchTerm(newSearchValue);
        }
        else {
            setSearchTerm(newSearchValue);
        }
    }

    const handleSearchSelector = (event) => {
        if (searchContext?.isInUse) {
            searchContext.updateSearchColumn(event.target.value);
        }
        else {
            setInitialSelectValue(event.target.value)
            setSearchColumn(event.target.value)
        }
    }
    // end of search context


    return <div className="flex flex-row justify-between mb-3" id="pageContainer">
        <div className="flex flex-row justify-between" id="allFiltersLabel">
            <SearchField
                cyName={cyName}
                setSearchTerm={handleSearch}
                searchTerm={searchContext?.isInUse ? searchContext.searchTerm.value : newSearchTerm?.value}
                disabled={searchContext.disableSearch}></SearchField>
            <select
                id='searchDropDownOptions'
                value={searchContext?.isInUse ? searchContext.searchTerm.searchOnName : searchDescriptorOptions?.[0]?.searchOnName}
                className={`border text-gray-700 bg-white text-xs ml-2 p-1 rounded flex flex-grow cursor-pointer ${searchContext.disableSearch ? 'cursor-not-allowed' : ''}`}
                onChange={handleSearchSelector}
                disabled={searchContext.disableSearch}>
                {searchContext?.isInUse ? buildColumnSearchDropDown(searchContext.searchDescriptor) : searchDescriptorOptions}
            </select>
            {showFilterMenu ? <button type="button" title={translate.byKey(TranslationKeys.applyQuickFilter)} className="flex flex-row items-center gap-1 ml-2 px-2 py-1 border text-gray-700 rounded text-xs"
                onClick={() => toggleFilterModal("")}>
                <span>{translate.byKey(TranslationKeys.filter)}</span>
                <img src={filterIcon} className="h-4 w-4" alt="" />
            </button> : null}
            {tableContext.canExportReport &&
                <DropdownSelector className="flex flex-row justify-between items-center ml-2 p-1 border text-portal-blue-primary rounded"
                    onSelected={(event) => generateReport(event)}
                    options={downloadOptions}
                    width={tableContext.exportVisibleData ? 'w-48' : 'w-24'}>
                    <img className="" src={downloadIcon} alt="Download Report" />
                </DropdownSelector>}

            {onTagSearchOpen ?
                <button title={translate.byKey(TranslationKeys.scanCard)} type="button" className="flex flex-row justify-between items-center ml-2 px-1 border text-gray-700 rounded"
                    onClick={onTagSearchOpen}>
                    <img src={CardIcon} className="h-5 w-5" alt="" />
                </button> : null}
            {onCardProvision ? <button id="backBtn" type="button" className="flex flex-row justify-between items-center ml-2 px-1 border rounded" title={translate.byKey(TranslationKeys.ossCardProvision)} onClick={onCardProvision}>
                <img src={CardProvisionIcon} alt="Provision Card" className="h-5 w-6" />
            </button> : null}
        </div>
        {children}
    </div>;
};

export default SearchBar;
